import { Get } from 'config/apiServiceConfig'
import axiosInstanceApiGateway from 'config/apiServiceApiGateway'
import { OrderV2OrderDetailResponseType } from 'pages/Order/components/Detail/mapOrderProducts'
import { OrderResponseType } from '../utils/types'
import { getAvailableStaff } from './staffAvailability'

type GetOrderDetailResponseType = {
  type: string
  invoice: string
  sla: string
  grabOrderId: string
  grabBookingCode: string
  orderCreatedAt: number
  status: string
  note: string
  referenceOrder: string
  customer: {
    name: string
    phone: string
    address: string
    latitude: number
    longitude: number
    addressDetail: string
    driverNote: string
  }
  payment: {
    method: string
    name: string
    amount: number
    partner: string
    deliveryFee: number
    astroBalance: number
    totalCoinUsed: number
    totalCoinEarned: number
    totalPriceItem: number
    platformFee: {
      amount: number
      paymentFee: number
      packagingFee: number
    }
  }
  scheduledInstant: boolean
  hybrid: boolean
}

export type GetOrderListV2ParamsType = {
  location_id: string
  limit?: number
  offset?: number
  section?: string
  isFulfillment?: boolean
  order_id?: string
}

export type PayloadStagingAreaThunkType = {
  data: {
    orderId: string
    location_id: string
    staging_area: string
  }
  successCallback: Nullable<() => void>
}

export type PostPickPickerV2PayloadType = {
  fulfillment_id?: string
  picker_id: number
  reason?: string
  astro_approval_pin_authentication?: {
    phone_number: string
    location_id: number
    pin: string
  }
}

export type PostPickPackerV2PayloadType = {
  order_id: string
  packer_id: number
}

export type GetStaffListPayloadType = {
  location_id: string
  role: 'PICKER' | 'PACKER' | 'DRIVER'
  limit?: number
}

export type GetListPickingItemType = {
  rack_name: string
  special_type: string
  quantity: number
  sku: string
  product_id: number
  product_name: string
  product_brand: string
}

export type GetListPickingResponseType = {
  items: GetListPickingItemType[]
  fulfillment_id: number
  error: {
    status: boolean
    message: string
    code: number
  }
  note: string
  payment_at: number
  invoice: string
}

export type AstroApprovalPinAuthenticationPayloadType = {
  astro_approval_pin_authentication: {
    phone_number: string
    location_id: number
    pin: string
  }
}

export const getOrderListV2 = (params: GetOrderListV2ParamsType) =>
  axiosInstanceApiGateway.Get<OrderResponseType, true>({
    url: `/fulfillment/internal/v1/admin/orders`,
    params,
  })

export const getPickerDetailV2 = (orderID: string) => {
  axiosInstanceApiGateway.Get<{ order_id: string; fulfillment_id: string }>({
    url: `/fulfillment/internal/v1/admin/orders/${orderID}/picker`,
  })
}

export const getOrderDetail = (id: number) =>
  Get<GetOrderDetailResponseType>({ url: `/api/v2/order/${id}` })
export const fetchOrderProducts = (id: number) =>
  axiosInstanceApiGateway.Get<OrderV2OrderDetailResponseType, true>({
    url: `/oms/internal/v2/orders/${id}`,
  })

export const postQRPrinting = (id: string) =>
  axiosInstanceApiGateway.Post({ url: `/fulfillment/internal/v1/printing/orders/${id}/print` })

export const getStaffList = (payload: GetStaffListPayloadType) =>
  getAvailableStaff(payload.location_id, payload.role)

// new order apis

export const postPickPickerV2 = (payload: PostPickPickerV2PayloadType) =>
  axiosInstanceApiGateway.Post({
    url: `/fulfillment/internal/v1/admin/fulfillments/${payload.fulfillment_id}/picking/assign-picker`,
    data: payload,
  })

export const postPickPickerWithoutPinV2 = (payload: PostPickPickerV2PayloadType) =>
  axiosInstanceApiGateway.Post({
    url: `/fulfillment/internal/v1/admin/fulfillments/${payload.fulfillment_id}/picking/assign-picker-without-pin`,
    data: payload,
  })

export type ReassignPickerPayloadType = {
  fulfillment_id: string
  new_picker_id: number
  set_old_picker_to_unavailable: boolean
  reason?: string
  astro_approval_pin_authentication: NonNullable<
    PostPickPickerV2PayloadType['astro_approval_pin_authentication']
  >
}

export const putReassignPickerV2 = ({ fulfillment_id, ...data }: ReassignPickerPayloadType) =>
  axiosInstanceApiGateway.Put({
    url: `/fulfillment/internal/v1/admin/fulfillments/${fulfillment_id}/picking/reassign-picker`,
    data,
  })

// picking

export const putPickPickerV2 = (payload: PostPickPickerV2PayloadType) =>
  axiosInstanceApiGateway.Put({
    url: `/fulfillment/internal/v1/admin/fulfillments/${payload.fulfillment_id}/picking/change-picker`,
    data: payload,
  })

export const postUpdateStatusUpcoming = (fulfillmentId: string) =>
  axiosInstanceApiGateway.Put({
    url: `/fulfillment/internal/v1/admin/fulfillments/${fulfillmentId}/update-to-new-order`,
  })

// packing

export const postStartPackingV2 = (
  fulfillmentID: string,
  payload?: AstroApprovalPinAuthenticationPayloadType,
) =>
  axiosInstanceApiGateway.Post({
    url: `/fulfillment/internal/v1/admin/fulfillments/${fulfillmentID}/packing/start`,
    data: payload,
  })

export const postPickPackerV2 = (payload: PostPickPackerV2PayloadType) =>
  axiosInstanceApiGateway.Post({
    url: `/fulfillment/internal/v1/packing/orders/${payload.order_id}/assign-packer`,
    data: payload,
  })

export const putPickPackerV2 = (payload: PostPickPackerV2PayloadType) =>
  axiosInstanceApiGateway.Put({
    url: `/fulfillment/internal/v1/packing/orders/${payload.order_id}/change-packer`,
    data: payload,
  })

export const getOrderListLogisticV2 = (params: GetOrderListV2ParamsType) =>
  axiosInstanceApiGateway.Get<OrderResponseType, true>({
    url: `/logistic/internal/v1/admin/orders`,
    params,
  })

export const getListPicking = (fulfillment_id: string) =>
  axiosInstanceApiGateway.Get<GetListPickingResponseType, true>({
    url: `/fulfillment/internal/v1/admin/fulfillments/${fulfillment_id}/picking`,
  })
